import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { I18N_KEYS } from '@/language/keys';
import { validateFile } from '@/utils/nodeInputValidation';
import { HandleType } from '@/enums/handle-type.enum';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { log } from '@/logger/logger';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useDeleteAssetsSubMenu } from '@/hooks/useDeleteAssetsSubMenu';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { FF_PACK_UNPACK_NODE } from '@/consts/featureFlags';
import { FileUploaderV2 } from '../../Common/FileUploaderV2';
import { useImportView, useEffectiveIsLocked } from '../../Recipe/FlowContext';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { useCreateIteratorFromNode, getIteratorInfo } from '../useCreateIteratorFromNode';
import { useUnpackNode } from '../useUnpackNode';
import { ImportNodeViewer } from './ImportNodeViewer';
import type { PackUnpackItem } from '../packUnpack.consts';
import type { Handle, ImportData } from '@/types/node';
import type { MenuAction } from '@/components/Menu/Actions';
import type { MediaAsset } from '@/types/api/assets';

const logger = log.getLogger('ImportNode');
function ImportNode({
  id,
  data,
  updateNodeData,
}: {
  id: string;
  data: ImportData;
  updateNodeData: (id: string, data: Partial<ImportData>) => void;
}) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const recipeName = useWorkflowStore((state) => state.recipe?.name);
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();
  const importView = useImportView(id);
  const [isUploadFormUploading, setIsUploadFormUploading] = useState(false);
  const { viewMode = NodeViewMode.Single } = data;
  const { handles, pastedData, initialData, selectedIndex, files } = data;
  // if node has only one file, always display in single view mode.
  const displayViewMode = files.length === 1 ? NodeViewMode.Single : viewMode;
  const isLocked = useEffectiveIsLocked(id);
  const editable = isLocked !== true && role === 'editor';
  const isUnpackEnabled = useFeatureFlagEnabled(FF_PACK_UNPACK_NODE);

  const selectedFile = files[selectedIndex];

  const onUpload = useCallback(
    (file: Partial<MediaAsset> | null) => {
      const fileKind = validateFile(file);
      logger.info('file upload success', { fileKind });
      if (fileKind) {
        importView.addFile(fileKind, false);
      }
    },
    [importView],
  );

  useEffect(() => {
    // Handle 3D file specific logic
    const hasImageHandle = handles?.output?.['image'] !== undefined;
    if (selectedFile?.type === '3D' && !hasImageHandle) {
      const handle: Handle = {
        description: '',
        format: 'uri',
        id: uuidv4(),
        label: 'image',
        order: 1,
        required: false,
        type: HandleType.Image,
      };

      updateNodeData(id, {
        handles: {
          ...handles,
          output: {
            ...handles?.output,
            image: handle,
          },
        },
      });
    }
  }, [id, updateNodeData, handles, selectedFile]);

  useEffect(() => {
    if (initialData) {
      const fileKind = validateFile(initialData);
      if (fileKind) {
        importView.setFiles([fileKind], undefined, false);
      }
    }
  }, [id, importView, initialData]);

  const onInitialFileUpload = useCallback(
    (hadError: boolean) => {
      if (hadError) {
        importView.clearPastedAndInitialData();
      }
    },
    [importView],
  );

  const { unpackNode } = useUnpackNode();

  const handleUnpack = useCallback(() => {
    unpackNode(
      id,
      files.map((file) => ({ type: 'file', file })),
    );
  }, [unpackNode, id, files]);

  const unpackMenuItems = useMemo<MenuAction[]>(() => {
    if (!isUnpackEnabled) return [];
    return [
      { type: 'divider' },
      { name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.UNPACK), action: handleUnpack, disabled: files.length <= 1 },
    ];
  }, [isUnpackEnabled, files.length, handleUnpack, t]);

  const { createIterator } = useCreateIteratorFromNode();

  const iteratorItems = useMemo<PackUnpackItem[]>(() => files.map((file) => ({ type: 'file', file })), [files]);

  const iteratorInfo = useMemo(() => getIteratorInfo(iteratorItems), [iteratorItems]);

  const handleCreateIterator = useCallback(() => {
    createIterator(id, iteratorItems);
  }, [createIterator, id, iteratorItems]);

  const createIteratorMenuItems = useMemo<MenuAction[]>(() => {
    if (!isUnpackEnabled) return [];
    return [
      {
        name: t(iteratorInfo?.labelKey ?? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.CREATE_ITERATOR),
        action: handleCreateIterator,
        disabled: !iteratorInfo,
      },
    ];
  }, [isUnpackEnabled, iteratorInfo, handleCreateIterator, t]);

  const deleteAssetsMenuItems = useDeleteAssetsSubMenu({
    nodeId: id,
    view: {
      deleteAll: () => importView.deleteAll(),
      deleteAllOthers: () => importView.deleteAllOthers(),
      deleteCurrent: () => importView.deleteCurrent(),
      assets: files,
    },
    allowDeletingLastFile: false,
  });

  const handleDownloadCurrent = useCallback(async () => {
    const file = importView.getCurrentFile();
    if (!file) return;

    await downloadAssetFromNode({
      url: file.url,
      fileType: file.type,
      nodeName: data.name || id,
      prefix: recipeName,
    });
  }, [importView, data.name, id, recipeName, downloadAssetFromNode]);

  const handleDownloadAll = useCallback(async () => {
    const files = importView.getFilesList();
    await downloadAssetsFromNodeWithProgress(files, data.name || id, recipeName, undefined);
  }, [importView, data.name, id, recipeName, downloadAssetsFromNodeWithProgress]);

  return (
    <DynamicNode2
      id={id}
      data={data}
      className="import"
      additionalMenu={[...unpackMenuItems, ...createIteratorMenuItems, ...deleteAssetsMenuItems]}
    >
      {files.length > 0 && selectedFile && !isUploadFormUploading ? (
        <ImportNodeViewer
          id={id}
          files={files}
          selectedIndex={selectedIndex}
          onUpload={onUpload}
          viewMode={displayViewMode}
          selectedViewMode={viewMode}
          onDownloadCurrent={handleDownloadCurrent}
          onDownloadAll={handleDownloadAll}
        />
      ) : (
        <FileUploaderV2
          allowUrlInput
          disabled={!editable}
          id={id}
          initialFile={(pastedData?.imageFile as File) || undefined}
          onUpload={onUpload}
          value={selectedFile}
          isFileValid={(file) => {
            return validateFile(file) !== undefined;
          }}
          setIsUploading={setIsUploadFormUploading}
          isUploading={isUploadFormUploading}
          onInitialFileUpload={onInitialFileUpload}
        />
      )}
    </DynamicNode2>
  );
}

export default ImportNode;
